import React, { useMemo } from "react";

import { faCircleExclamation } from "@performyard/fontawesome/pro-regular-svg-icons";
import { PageBanner, Spinner, Table, Text } from "@performyard/py-components";
import type { FieldHistoryImportError } from "@performyard/service-contracts";

import pluralizer from "../../../../utils/pluralizer";

type ValidationErrorRow = {
	rowNumber: string;
	message: string;
};

type ValidationResultsProps = {
	status: "idle" | "validating" | "invalid";
	/** Null until a file is chosen; both states whose copy names a file always have it. */
	fileName: string | null;
	errors: FieldHistoryImportError[];
	/** May exceed `errors.length` — the server caps what it sends back. */
	totalErrorCount: number;
};

// Hoisted: a new array literal each render defeats Table's internal useMemo. No `type: "text"` on
// Error — that renderer clamps to a single line, and the longest messages need to wrap.
const ERROR_COLUMN_DEFS = [
	{ headerName: "Row number", field: "rowNumber", type: "text", width: 140, minWidth: 140, maxWidth: 140 },
	{ headerName: "Error", field: "message", flex: 1, wrapText: true, autoHeight: true },
];

const ValidationResults = ({ status, fileName, errors, totalErrorCount }: ValidationResultsProps) => {
	const rowData = useMemo<ValidationErrorRow[]>(
		() =>
			errors.map((error) => ({
				rowNumber: error.rowNumber !== undefined ? String(error.rowNumber) : "—",
				message: error.message,
			})),
		[errors]
	);

	const hiddenErrorCount = Math.max(totalErrorCount - errors.length, 0);
	const errorSummary = `Found ${totalErrorCount} ${pluralizer(totalErrorCount, "error", "errors")}`;

	// No heading: the section reads as the tail of the page rather than a second place to drop a file.
	return (
		<section aria-label="Validation results" className="flex flex-col gap-4">
			{status === "idle" && (
				<Text size="paragraph-3" weight="regular" color="neutral-7">
					Results appear here after you choose a file.
				</Text>
			)}
			{/* Announcements come from the page's always-mounted live region. */}
			{status === "validating" && (
				<div className="flex items-center gap-3">
					<Spinner size={20} />
					<Text size="paragraph-3" weight="regular" color="neutral-7">
						Checking {fileName}…
					</Text>
				</div>
			)}
			{status === "invalid" && (
				<>
					<PageBanner
						color="red"
						icon={faCircleExclamation}
						mainTitle={fileName ? `${errorSummary} in ${fileName}` : errorSummary}
						subTitle="Nothing was imported. Fix your file and choose it again."
					/>
					{hiddenErrorCount > 0 && (
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							{`Showing the first ${errors.length}; ${hiddenErrorCount} more ${pluralizer(hiddenErrorCount, "is", "are")} not listed.`}
						</Text>
					)}
					<div className="h-[480px]">
						<Table<ValidationErrorRow>
							columnSizing="fit-grid"
							rowData={rowData}
							columnDefs={ERROR_COLUMN_DEFS}
							animateRows={false}
						/>
					</div>
				</>
			)}
		</section>
	);
};

export default ValidationResults;
